/**
 * Fraction class for exact arithmetic using BigInt
 * Implements precise fractional calculations to avoid floating-point errors
 * Essential for cutting plane algorithms where precision matters
 */
export class Fraction {
  private readonly numerator: bigint
  private readonly denominator: bigint

  constructor(numerator: bigint | number | string, denominator: bigint | number | string = 1n) {
    const num = typeof numerator === 'bigint' ? numerator : BigInt(numerator)
    const den = typeof denominator === 'bigint' ? denominator : BigInt(denominator)
    
    if (den === 0n) {
      throw new Error('Denominator cannot be zero')
    }
    
    // Normalize sign (denominator should be positive)
    const sign = den < 0n ? -1n : 1n
    const absNum = num * sign
    const absDen = den * sign
    
    // Simplify the fraction
    const gcd = this.gcd(absNum < 0n ? -absNum : absNum, absDen)
    
    this.numerator = absNum / gcd
    this.denominator = absDen / gcd
  }

  /**
   * Create a Fraction from a decimal number (with potential precision loss warning)
   */
  static fromDecimal(decimal: number, _maxDenominator: number = 1000000): Fraction {
    if (!Number.isFinite(decimal)) {
      throw new Error('Cannot convert non-finite number to fraction')
    }
    
    if (decimal === 0) {
      return new Fraction(0n, 1n)
    }
    
    // Convert decimal to fraction using continued fractions algorithm
    let sign = decimal < 0 ? -1 : 1
    decimal = Math.abs(decimal)
    
    let wholePart = Math.floor(decimal)
    let fractionalPart = decimal - wholePart
    
    if (fractionalPart === 0) {
      return new Fraction(BigInt(sign * wholePart), 1n)
    }
    
    // Simple approximation for now - can be improved with continued fractions
    const factor = Math.pow(10, decimal.toString().split('.')[1]?.length || 0)
    const num = Math.round(decimal * factor)
    const den = factor
    
    return new Fraction(BigInt(sign * num), BigInt(den))
  }

  /**
   * Create zero fraction
   */
  static zero(): Fraction {
    return new Fraction(0n, 1n)
  }

  /**
   * Create one fraction
   */
  static one(): Fraction {
    return new Fraction(1n, 1n)
  }

  /**
   * Get the numerator
   */
  getNumerator(): bigint {
    return this.numerator
  }

  /**
   * Get the denominator
   */
  getDenominator(): bigint {
    return this.denominator
  }

  /**
   * Addition
   */
  add(other: Fraction): Fraction {
    const num = this.numerator * other.denominator + other.numerator * this.denominator
    const den = this.denominator * other.denominator
    return new Fraction(num, den)
  }

  /**
   * Subtraction
   */
  subtract(other: Fraction): Fraction {
    const num = this.numerator * other.denominator - other.numerator * this.denominator
    const den = this.denominator * other.denominator
    return new Fraction(num, den)
  }

  /**
   * Multiplication
   */
  multiply(other: Fraction): Fraction {
    const num = this.numerator * other.numerator
    const den = this.denominator * other.denominator
    return new Fraction(num, den)
  }

  /**
   * Division
   */
  divide(other: Fraction): Fraction {
    if (other.numerator === 0n) {
      throw new Error('Division by zero')
    }
    const num = this.numerator * other.denominator
    const den = this.denominator * other.numerator
    return new Fraction(num, den)
  }

  /**
   * Negation
   */
  negate(): Fraction {
    return new Fraction(-this.numerator, this.denominator)
  }

  /**
   * Absolute value
   */
  abs(): Fraction {
    return new Fraction(this.numerator < 0n ? -this.numerator : this.numerator, this.denominator)
  }

  /**
   * Floor function - returns the largest integer <= this fraction
   */
  floor(): Fraction {
    if (this.numerator >= 0n) {
      return new Fraction(this.numerator / this.denominator, 1n)
    } else {
      // For negative numbers, we need to round down
      const quotient = this.numerator / this.denominator
      const remainder = this.numerator % this.denominator
      return new Fraction(remainder === 0n ? quotient : quotient - 1n, 1n)
    }
  }

  /**
   * Ceiling function - returns the smallest integer >= this fraction
   */
  ceil(): Fraction {
    if (this.numerator >= 0n) {
      const quotient = this.numerator / this.denominator
      const remainder = this.numerator % this.denominator
      return new Fraction(remainder === 0n ? quotient : quotient + 1n, 1n)
    } else {
      return new Fraction(this.numerator / this.denominator, 1n)
    }
  }

  /**
   * Fractional part - returns this - floor(this)
   * This is crucial for cutting plane generation
   */
  fractionalPart(): Fraction {
    return this.subtract(this.floor())
  }

  /**
   * Comparison functions
   */
  equals(other: Fraction): boolean {
    return this.numerator * other.denominator === other.numerator * this.denominator
  }

  lessThan(other: Fraction): boolean {
    return this.numerator * other.denominator < other.numerator * this.denominator
  }

  lessThanOrEqual(other: Fraction): boolean {
    return this.numerator * other.denominator <= other.numerator * this.denominator
  }

  greaterThan(other: Fraction): boolean {
    return this.numerator * other.denominator > other.numerator * this.denominator
  }

  greaterThanOrEqual(other: Fraction): boolean {
    return this.numerator * other.denominator >= other.numerator * this.denominator
  }

  /**
   * Check if this fraction is zero
   */
  isZero(): boolean {
    return this.numerator === 0n
  }

  /**
   * Check if this fraction is one
   */
  isOne(): boolean {
    return this.numerator === this.denominator
  }

  /**
   * Check if this fraction is an integer
   */
  isInteger(): boolean {
    return this.denominator === 1n
  }

  /**
   * Check if this fraction is positive
   */
  isPositive(): boolean {
    return this.numerator > 0n
  }

  /**
   * Check if this fraction is negative
   */
  isNegative(): boolean {
    return this.numerator < 0n
  }

  /**
   * Check if this fraction has numerically unstable coefficients
   * Returns true if the numerator or denominator exceeds safe limits
   */
  isNumericallyUnstable(threshold: number = 1e12): boolean {
    const numAbs = this.numerator < 0n ? -this.numerator : this.numerator
    const denAbs = this.denominator < 0n ? -this.denominator : this.denominator
    
    // Convert to number for comparison (safe since we're just checking magnitude)
    const numFloat = Number(numAbs)
    const denFloat = Number(denAbs)
    
    return numFloat > threshold || denFloat > threshold
  }

  /**
   * Convert to a numerically stable decimal with controlled precision
   * Use this when exact fraction arithmetic becomes unwieldy
   */
  toStableDecimal(precision: number = 10): number {
    // If fraction is stable, use normal conversion
    if (!this.isNumericallyUnstable()) {
      return this.toNumber()
    }
    
    // For unstable fractions, use controlled precision
    const decimal = this.toNumber()
    const factor = Math.pow(10, precision)
    return Math.round(decimal * factor) / factor
  }

  /**
   * Create a numerically stable fraction from a potentially unstable one
   */
  static createStable(fraction: Fraction, maxDenominator: number = 1e6): Fraction {
    if (!fraction.isNumericallyUnstable()) {
      return fraction
    }
    
    // Convert to decimal and back to fraction with controlled precision
    const decimal = fraction.toNumber()
    return Fraction.fromDecimal(decimal, maxDenominator)
  }

  /**
   * Convert to JavaScript number (may lose precision)
   */
  toNumber(): number {
    return Number(this.numerator) / Number(this.denominator)
  }

  /**
   * Convert to string representation
   */
  toString(): string {
    if (this.denominator === 1n) {
      return this.numerator.toString()
    }
    return `${this.numerator}/${this.denominator}`
  }

  /**
   * Convert to LaTeX string for mathematical display
   */
  toLatex(): string {
    if (this.denominator === 1n) {
      return this.numerator.toString()
    }
    return `\\frac{${this.numerator}}{${this.denominator}}`
  }

  /**
   * Greatest Common Divisor using Euclidean algorithm
   */
  private gcd(a: bigint, b: bigint): bigint {
    while (b !== 0n) {
      const temp = b
      b = a % b
      a = temp
    }
    return a
  }

  /**
   * Least Common Multiple
   */
  static lcm(a: bigint, b: bigint): bigint {
    return (a * b) / Fraction.prototype.gcd(a, b)
  }

  /**
   * Create a fraction from a string representation like "3/4" or "5"
   */
  static fromString(str: string): Fraction {
    const trimmed = str.trim()
    
    if (trimmed.includes('/')) {
      const parts = trimmed.split('/')
      if (parts.length !== 2) {
        throw new Error(`Invalid fraction string: ${str}`)
      }
      return new Fraction(BigInt(parts[0]), BigInt(parts[1]))
    } else {
      return new Fraction(BigInt(trimmed), 1n)
    }
  }

  /**
   * Find the minimum of multiple fractions
   */
  static min(...fractions: Fraction[]): Fraction {
    if (fractions.length === 0) {
      throw new Error('Cannot find minimum of empty array')
    }
    
    let min = fractions[0]
    for (let i = 1; i < fractions.length; i++) {
      if (fractions[i].lessThan(min)) {
        min = fractions[i]
      }
    }
    return min
  }

  /**
   * Find the maximum of multiple fractions
   */
  static max(...fractions: Fraction[]): Fraction {
    if (fractions.length === 0) {
      throw new Error('Cannot find maximum of empty array')
    }
    
    let max = fractions[0]
    for (let i = 1; i < fractions.length; i++) {
      if (fractions[i].greaterThan(max)) {
        max = fractions[i]
      }
    }
    return max
  }
}

/**
 * Helper for rendering fractions in the UI.
 *
 * Rules:
 * - If denominator == 1, show integer (e.g. "3", "-2")
 * - If denominator is small (<= maxDenominator), show "p/q"
 * - If denominator is large, show decimal with limited precision
 */
export function formatFractionForDisplay(
  f: Fraction,
  opts: { maxDenominator?: number; decimalPlaces?: number } = {}
): string {
  const maxDen = opts.maxDenominator ?? 20
  const decimalPlaces = opts.decimalPlaces ?? 4

  const den = f.getDenominator()
  const num = f.getNumerator()

  if (den === 1n) {
    return num.toString()
  }

  const absDen = den < 0n ? -den : den
  if (absDen <= BigInt(maxDen)) {
    // Reasonable denominator: keep exact fractional form.
    return f.toString()
  }

  // Large denominator: show decimal with limited precision.
  const val = f.toStableDecimal(decimalPlaces)
  let s = val.toFixed(decimalPlaces)
  // Trim trailing zeros and optional dot.
  s = s.replace(/\.?0+$/, '')
  return s
}
